import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { ClientType } from "@/types/db/clients";
import useToast from "@/hooks/useToast";
import { EstimateForm, EstimatePayload } from "./types";
import { EstimateFields } from "./EstimateFields";
import { FixedAttachment } from "@/components/Attachment/FixedAttachment";
import { AttachmentFile } from "@/components/Attachment/types";
import { ThemedText } from "@/components/ThemedText";
import { theme } from "@/constants/themes";

export { EstimatePayload } from "./types";
export type { EstimateForm } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "DRAFT", color: "todo" },
  { text: "SENT", color: "pending" },
  { text: "ACCEPTED", color: "complete" },
  { text: "REJECTED", color: "attention" },
  { text: "EXPIRED", color: "neutral" },
] as const;

export function getEstimateStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    DRAFT: colors.todo.border,
    SENT: colors.pending.border,
    ACCEPTED: colors.complete.border,
    REJECTED: colors.attention.border,
    EXPIRED: colors.neutral.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const ESTIMATE_STATUS_OPTIONS = STATUS_OPTIONS;

export interface EstimateCreateFormProps {
  initialEstimate?: Partial<EstimateForm>;
  onSave?: (payload: { estimate: EstimateForm }) => Promise<void> | void;
  onChange?: (payload: EstimatePayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onClientChange?: (client: ClientType | null) => void;
}

export interface EstimateCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateEstimateName: (name: string) => void;
  updateStatus: (status: string) => void;
  openClientModal: () => void;
  getPendingFiles: () => AttachmentFile[];
}

export const EstimateCreateForm = forwardRef<EstimateCreateFormRef, EstimateCreateFormProps>(
  ({ initialEstimate, onSave, onChange, onDirtyChange, onClientChange }, ref) => {
    const toast = useToast();
    const [attachmentFiles, setAttachmentFiles] = useState<AttachmentFile[]>([]);

    // ---------- form state ----------
    const [estimate, setEstimate] = useState<EstimateForm>({
      estimate_number: initialEstimate?.estimate_number ?? "",
      client_id: initialEstimate?.client_id ?? "",
      project_id: initialEstimate?.project_id ?? "",
      status: initialEstimate?.status ?? "DRAFT",
      meta_data: {
        ...initialEstimate?.meta_data,
        estimate_number: initialEstimate?.estimate_number,
        status: initialEstimate?.status ?? "DRAFT",
        line_items: initialEstimate?.meta_data?.line_items ?? [],
      },
    });

    useEffect(() => {
      onChange?.({ estimate });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimate]);

    const baseline = useRef(JSON.stringify({ estimate }));
    const dirty = useMemo(() => JSON.stringify({ estimate }) !== baseline.current, [estimate]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateEstimateName: (name: string) => {
        setEstimate((e) => ({ ...e, estimate_number: name }));
      },
      updateStatus: (status: string) => {
        setEstimate((e) => ({ ...e, status: status as EstimateForm["status"] }));
      },
      openClientModal: () => {
        // Client selection is now inline via ClientInput
      },
      getPendingFiles: () => attachmentFiles,
    }));

    // ---------- helpers ----------
    const updateEstimate = (patch: Partial<EstimateForm>) => setEstimate((e) => ({ ...e, ...patch }));

    const handleSave = async () => {
      // Validations
      if (!estimate.client_id?.trim()) {
        toast.showToast({
          title: "Missing Client",
          message: "Please select a client for this estimate",
          toastType: "warning",
        });
        return;
      }
      if (!estimate.project_id?.trim()) {
        toast.showToast({
          title: "Missing Project",
          message: "Please select a project for this estimate",
          toastType: "warning",
        });
        return;
      }
      if (!estimate.status) {
        toast.showToast({
          title: "Missing Status",
          message: "Please select a status for this estimate",
          toastType: "warning",
        });
        return;
      }

      try {
        await onSave?.({ estimate });
        baseline.current = JSON.stringify({ estimate });
      } catch (e: any) {
        throw e; // Re-throw to let parent handle it
      }
    };

    const handleReset = () => {
      const nextEstimate: EstimateForm = {
        estimate_number: initialEstimate?.estimate_number ?? "",
        client_id: initialEstimate?.client_id ?? "",
        project_id: initialEstimate?.project_id ?? "",
        status: initialEstimate?.status ?? "DRAFT",
        meta_data: {
          ...initialEstimate?.meta_data,
          estimate_number: initialEstimate?.estimate_number,
          status: initialEstimate?.status ?? "DRAFT",
          line_items: initialEstimate?.meta_data?.line_items ?? [],
        },
      };
      setEstimate(nextEstimate);
      setAttachmentFiles([]);
      onClientChange?.(null);
      baseline.current = JSON.stringify({ estimate: nextEstimate });
    };

    return (
      <View style={styles.container}>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ minHeight: "100%", gap: 16 }}>
          <EstimateFields estimate={estimate} updateEstimate={updateEstimate} />

          {/* Attachments Section */}
          <View style={{ gap: 8 }}>
            <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
              Attachments
            </ThemedText>
            <ThemedText style={{ fontSize: 12, opacity: 0.7 }}>
              Files will be uploaded when you save the estimate
            </ThemedText>
            <FixedAttachment
              files={attachmentFiles}
              onFilesChange={setAttachmentFiles}
              autoUpload={false}
              maxFiles={10}
              maxFileSize={50 * 1024 * 1024}
              mode="grid"
              showPreviews={true}
              dragDropEnabled={true}
              showUploadButton={true}
              placeholder="Add files to this estimate"
              testID="estimate-attachment"
            />
          </View>
        </ScrollView>
      </View>
    );
  },
);

EstimateCreateForm.displayName = "EstimateCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
